/**
 * Wire schemas for the Operations **Placed book** surface — the census of
 * the Placed book.
 *
 * The Placed book is the thickest, slowest-moving part of the pipeline: the
 * ~180 cases a Handling Firm has officially taken (FSA returned, flipped to
 * **Placed**) and is now working through treatment / litigation toward
 * settlement. By construction this is the part of the pipeline that needs
 * the LEAST active attention — so the surface is a *monitoring census*, not
 * a worklist. It answers one question: "what's the shape of the Placed book,
 * and how long has each case been sitting in it?"
 *
 * Unlike the Under Review book (routing bloat) and Accepted book (FSA chase), there is no
 * worst-first action queue here — nothing to do per row. Cases are bucketed
 * into four **age bands** by time-in-Placed; you glance at the counts, then
 * click a band to read down it. The attention gradient is built into the
 * bands: a freshly-placed case (≤30d) may still have loose ends to settle,
 * while a case a year deep is just churning along.
 *
 * Monthly-update compliance — who hasn't filed this month's required update —
 * is a DIFFERENT job on a monthly clock, and gets its own surface. The
 * Placed book is deliberately just the age census.
 *
 * The age axis (`daysInPlaced`) has a data wrinkle the composer corrects:
 * the 2026-04-27 data migration stamped ~38% of the Placed book with the
 * migration date as their Placed Date, so those cases read as artificially
 * fresh. The composer detects the migration stamp and anchors those cases'
 * age on the lead's real Creation Date instead, flagging them
 * `ageEstimated` so the number is never presented as exact. See ADR-0045.
 *
 * All derivation is read-only and live — no stored Placed book state.
 *
 * Spec: engineering/command-center/docs/port-notes/holding-pen.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import {
  PANEL_DETAIL_LEAD_FIELDS,
  PLACEMENT_DETAIL_FIELDS,
} from "./placement-detail.js";

/**
 * The four age bands, in order, keyed by their time-in-Placed range. The
 * boundaries: ≤30 days, ≤6 months (182d), ≤1 year (365d), then beyond.
 * These are Chris's bands — a fresh case to watch, two middle bands that
 * recede, and a long tail. Used as the band identity AND the tab label.
 */
export const PLACED_BANDS = [
  "0-30d",
  "30d-6mo",
  "6mo-1yr",
  "1yr+",
] as const;
export const PlacedBandSchema = z.enum(PLACED_BANDS);
export type PlacedBand = z.infer<typeof PlacedBandSchema>;

/**
 * One Placed case in the census. The unit is the **placement** that landed
 * (Decision = Placed); its lead supplies the client identity and the
 * case-summary read, its accepting firm the handling-firm name. All joins
 * are done by the composer so the UI renders without its own lookups.
 */
export const PlacedCaseSchema = z.object({
  /** The landed Placement's record ID — the case's identity here. */
  id: z.string(),
  /** Lead record ID — for joining to other surfaces. */
  leadRecordId: z.string(),
  /** Stable human-readable Lead ID (Airtable primary text). */
  leadId: z.string(),
  /** Client name. */
  client: z.string(),
  /** Case-type label, or null. */
  caseType: z.string().nullable(),
  /** Narrative case summary — backs the reused case-summary side panel. */
  caseSummary: z.string().nullable(),
  /** Two-letter incident state — orientation chip. */
  incidentState: z.string().nullable(),
  /** Joined Originating Firm name, or null. */
  originatingFirmName: z.string().nullable(),
  /** Joined Handling Firm name (the firm working the case), or null. */
  handlingFirmName: z.string().nullable(),

  /** Raw Placed Date (ISO-8601) off the placement, or null. Shown as the
   *  placed-on date in the expanded row. For migrated cases this is the
   *  migration stamp (2026-04-27) — which is exactly why `daysInPlaced`
   *  does NOT derive from it for those rows. */
  placedDate: z.string().nullable(),

  /** SURFACE — days in Placed, from the corrected age anchor (real Placed
   *  Date normally; the lead's Creation Date for migrated cases). The
   *  band key and the within-band sort. Clamped ≥ 0. */
  daysInPlaced: z.number(),

  /** Which age band `daysInPlaced` falls in. */
  band: PlacedBandSchema,

  /** TRUE when `daysInPlaced` is an ESTIMATE — the case was migrated (its
   *  Placed Date is the 2026-04-27 stamp) so its age is anchored on the
   *  lead's Creation Date, accurate to a few days, not exact. The UI tags
   *  these "est." so a case near a band boundary isn't read as gospel. */
  ageEstimated: z.boolean(),

  // Panel-detail lead-context fields (Aesthetic Parity, Phase 2) — the joined
  // lead's transfer flag, received date, case file, and FSA document, defined
  // once in PANEL_DETAIL_LEAD_FIELDS. Additive (ADR-0133).
  ...PANEL_DETAIL_LEAD_FIELDS,

  // Class-B placement fields (slice 4 — panel detail). Filing deadline +
  // monthly-update cadence; null on placements that don't carry them.
  ...PLACEMENT_DETAIL_FIELDS,
});
export type PlacedCase = z.infer<typeof PlacedCaseSchema>;

/** A band and how many cases sit in it — the glance, for the tab badges. */
export const PlacedBandCountSchema = z.object({
  band: PlacedBandSchema,
  count: z.number(),
});
export type PlacedBandCount = z.infer<typeof PlacedBandCountSchema>;

/**
 * Payload for `GET /api/placed`. `cases` is the full Placed book,
 * sorted longest-in-Placed first; `counts` carries one entry per band in
 * band order (always all four, zero-filled) so the tabs render their
 * badges without scanning `cases`.
 */
export const PlacedResponseSchema = z.object({
  cases: z.array(PlacedCaseSchema),
  counts: z.array(PlacedBandCountSchema),
});
export type PlacedResponse = z.infer<typeof PlacedResponseSchema>;
